<script setup lang="ts">
import type { CabinClass, PassengerType } from '@optionway/models';
import { ChevronDown, User, X } from 'lucide-vue-next';
// UiRadioGroupItem enforces h-4 w-4 radio button styles — use the primitive directly for the full-width card layout
import { RadioGroupItem } from 'reka-ui';
import { breakpointsTailwind } from '@vueuse/core';

const prefix = 'components.search.passengersTypes';
const cabinClassPrefix = 'components.search.cabinClassSelector';

const properties = defineProps<{ compact?: boolean }>();

const adults = defineModel<PassengerType['adults']>('adults', { default: 0 });
const children = defineModel<PassengerType['children']>('children', { default: 0 });
const infants = defineModel<PassengerType['infants']>('infants', { default: 0 });
const active = defineModel<PassengerType['active']>('active');
const cabinClass = defineModel<CabinClass>('cabinClass', { default: 'ECONOMY' });

const searchCriteriaStore = useSearchCriteriaStore();
const { cabinClassList } = storeToRefs(searchCriteriaStore);

const breakpoints = useBreakpoints(breakpointsTailwind);
const isMobile = breakpoints.smaller('md');

const [isSheetOpen, toggleIsSheetOpen] = useToggle(false);

const emit = defineEmits(['popover-closed']);

const triggerReference = useTemplateRef('triggerReference');
const { focused } = useFocus(triggerReference);

const popoverFocusReference = useTemplateRef<HTMLElement>('popoverFocusReference');
const onPopoverOpen = (event: Event) => {
  event.preventDefault();
  popoverFocusReference.value?.focus();
};

watch(active, (newValue) => {
  if (newValue === undefined) return;
  if (isMobile.value && newValue !== isSheetOpen.value) {
    toggleIsSheetOpen(newValue);
    return;
  }
  if (!isMobile.value && newValue !== focused.value) {
    focused.value = newValue;
  }
});
watch(
  focused,
  (newValue) => {
    if (newValue !== active.value) {
      active.value = newValue;
    }
  },
  { flush: 'sync' },
);
watch(isSheetOpen, (newValue) => {
  if (isMobile.value && newValue !== active.value) {
    active.value = newValue;
  }
});
</script>

<template>
  <div class="md:hidden">
    <UiSheet v-model:open="isSheetOpen">
      <UiSheetTrigger
        as-child
        class="w-full"
      >
        <UiButton
          variant="secondary"
          color="gray"
          shape="lg"
          :class="
            cn(
              'flex w-full items-center justify-between border-gray-300 bg-white font-normal text-text-primary-900 focus-visible:ring-2 focus-visible:ring-brand-primary-700 focus-visible:ring-offset-0 data-[state=open]:ring-2 data-[state=open]:ring-brand-primary-700',
              properties.compact
                ? 'h-9 px-3 py-1.5 text-sm [&_svg]:size-4'
                : 'h-11 px-3.5 py-2.5 text-base [&_svg]:size-5',
            )
          "
        >
          <div class="flex items-center gap-2">
            <User class="size-5 text-gray-500" />
            <span class="min-w-24">
              {{ $t(`${prefix}.paxSummary`, adults + children + infants) }} -
              {{ $t(`${cabinClassPrefix}.${cabinClass}`) }}
            </span>
          </div>

          <ChevronDown class="size-5 text-gray-500" />
        </UiButton>
      </UiSheetTrigger>

      <UiSheetContent
        side="bottom"
        class="top-0 w-full overflow-y-auto"
        :hide-close="true"
      >
        <div class="flex items-center px-4 py-4">
          <h2 class="flex-1 text-center text-xl font-medium text-text-primary-900">
            {{ $t(`${prefix}.title`) }}
          </h2>
          <UiSheetClose class="rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none">
            <X class="h-4 w-4" />
          </UiSheetClose>
        </div>

        <div class="flex flex-col gap-5">
          <h2 class="text-3xl">
            {{ $t(`${prefix}.passengersTitle`) }}
          </h2>

          <SearchPassengersContent
            v-model:adults="adults"
            v-model:children="children"
            v-model:infants="infants"
          />

          <UiCustomSeparatorLine />

          <h2 class="text-3xl">{{ $t(`${prefix}.cabinClassTitle`) }}</h2>

          <UiRadioGroup
            v-model="cabinClass"
            class="gap-3"
          >
            <RadioGroupItem
              v-for="cabinClassItem in cabinClassList"
              :key="cabinClassItem"
              :value="cabinClassItem"
              :class="
                cn(
                  'flex w-full items-center justify-start gap-3 rounded-xl border bg-transparent px-4 py-4 text-base text-text-primary-900',
                  cabinClass === cabinClassItem ? 'border-brand-primary-600' : 'border-gray-400',
                )
              "
            >
              <template v-if="cabinClass === cabinClassItem">
                <SvgoSearchCheckbox
                  filled
                  :font-controlled="false"
                  class="h-5 w-5 text-brand-primary-700"
                />
              </template>
              <span
                v-else
                aria-hidden="true"
                class="block h-5 w-5 rounded-full border border-border"
              />
              <span :class="cabinClass === cabinClassItem && 'font-semibold'">
                {{ $t(`${cabinClassPrefix}.${cabinClassItem}`) }}
              </span>
            </RadioGroupItem>
          </UiRadioGroup>

          <UiButton
            class="w-full"
            @click="toggleIsSheetOpen(false)"
          >
            {{ $t(`${prefix}.validate`) }}
          </UiButton>
        </div>
      </UiSheetContent>
    </UiSheet>
  </div>

  <div class="hidden md:block">
    <UiPopover>
      <UiPopoverTrigger as-child>
        <UiButton
          ref="triggerReference"
          variant="secondary"
          color="gray"
          shape="lg"
          :class="
            cn(
              'flex w-full items-center justify-between border-gray-300 bg-white font-normal text-text-primary-900 hover:bg-white focus-visible:ring-2 focus-visible:ring-brand-primary-700 focus-visible:ring-offset-0 data-[state=open]:ring-2 data-[state=open]:ring-brand-primary-700',
              properties.compact
                ? 'h-9 px-3 py-1.5 text-sm [&_svg]:size-4'
                : 'h-11 px-3.5 py-2.5 text-base [&_svg]:size-5',
            )
          "
        >
          <div class="flex items-center gap-2">
            <User class="size-5 text-gray-500" />
            <span class="min-w-24">{{ $t(`${prefix}.paxSummary`, adults + children + infants) }}</span>
          </div>

          <ChevronDown class="size-5 text-gray-500" />
        </UiButton>
      </UiPopoverTrigger>
      <UiPopoverContent
        class="w-80 p-0 pt-2"
        :side-offset="8"
        :align="'start'"
        :loop="false"
        @open-auto-focus="onPopoverOpen"
        @close-auto-focus.prevent="emit('popover-closed')"
      >
        <!-- Focus landing pad: prevents reka-ui from auto-focusing the first NumberFieldInput (which selects its value) while keeping focus inside the popover so Tab navigation works. -->
        <div
          ref="popoverFocusReference"
          tabindex="0"
          class="size-0 overflow-hidden"
        />
        <SearchPassengersContent
          v-model:adults="adults"
          v-model:children="children"
          v-model:infants="infants"
          separator
        />
      </UiPopoverContent>
    </UiPopover>
  </div>
</template>
